/**
 * Home on a phone and an iPad, in the Season design: who needs a word first, then the next live and who is here right
 * now. Phone is one column with the docked "Invite people"; upright is two columns; on its side the needs list, the
 * opened need and the next live stand side by side like Mail. It reads the same data and does the same things as the
 * computer's Home.
 */
import { useState } from "react";
import { Link } from "react-router";
import { noteAboutOf } from "../../../shared/people-notes";
import type { HomeNeed } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { FlashsuiteMenu } from "../../components/FlashsuiteMenu";
import { InviteSheet, inviteTargetOf } from "../../components/InviteSheet";
import { DockedBar } from "../../components/season/DockedBar";
import { Empty } from "../../components/season/Empty";
import { Fold } from "../../components/season/Fold";
import { GroupedList } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { Metrics } from "../../components/season/Numbers";
import { Page } from "../../components/season/Page";
import { GroupHead, PageHead } from "../../components/season/PageHead";
import { Pane, Panes } from "../../components/season/Panes";
import { Button } from "../../components/ui/Button";
import { useToast } from "../../components/ui/Toast";
import { firstName, fmt, longDay } from "../../lib/when";
import { NoteSheet } from "../people/sheets";
import { HelloLine, useAutomations } from "./parts";
import { Alerts, FootRows, NeedRow, NextLive, NowCard } from "./seasonParts";
import { AnswerButton, NeedBody, NeedSheet, PeopleSheet, ResultsSheet } from "./seasonSheets";
import { SeasonFresh } from "./SeasonFresh";
import { greeting, useHome, useHomeDone } from "./useHome";
import "../../styles/season-home.css";

/** The needs a phone shows before "Show N more". */
const PHONE_NEEDS = 3;

export function SeasonHome({ layout }: { layout: SeasonLayout }) {
  const { user } = useAuth();
  const toast = useToast();
  // Why the whole time: Season's Home counts every room, as its results sheet says, whatever period the computer's Home last showed.
  const { data, failed, reload, setData } = useHome("all");
  const done = useHomeDone({ setData, reload });
  const [autos] = useAutomations();
  const [picked, setPicked] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const [folded, setFolded] = useState(true);
  const [results, setResults] = useState(false);
  const [people, setPeople] = useState<"registered" | "clicked" | null>(null);
  const [note, setNote] = useState<HomeNeed | null>(null);
  const [inviting, setInviting] = useState(false);

  const name = data?.greeting_name ?? user?.first_name ?? "";
  const hello = greeting(new Date().getHours());
  const title = name ? `${hello}, ${name}` : hello;
  const phone = layout === "phone";
  const menu = phone ? <FlashsuiteMenu compact /> : undefined;
  const date = longDay(Date.now() / 1000);

  if (!data) {
    return (
      <Page layout={layout} narrow className="hs">
        <PageHead eyebrow={date} title={title} right={menu} />
        {failed ? (
          <div className="k-card hs-state">
            <span>Home didn't load.</span>
            <Button onClick={reload}>Try again</Button>
          </div>
        ) : (
          <p className="k-sub" aria-busy="true">
            Loading
          </p>
        )}
      </Page>
    );
  }
  if (data.fresh) return <SeasonFresh layout={layout} data={data} name={name} date={date} menu={menu} />;

  const live = data.next_live;
  const needs = data.needs;
  const open = needs.find((n) => n.key === opened) ?? null;
  const current = needs.find((n) => n.key === picked) ?? needs[0] ?? null;
  const send = (need: HomeNeed) => {
    setOpened(null);
    setNote(need);
  };
  const finish = (need: HomeNeed) => {
    setOpened(null);
    void done(need.key, need.at);
  };
  const row = (n: HomeNeed, side: boolean) => (
    <NeedRow key={n.key} need={n} on={side && n.key === current?.key} onPick={() => (side ? setPicked(n.key) : setOpened(n.key))} />
  );
  const sub = (
    <p className="k-sub">
      <HelloLine watching={data.right_now.watching.count} needs={needs.length} />
    </p>
  );
  const needsHead = <GroupHead title="Needs a word" count={needs.length} />;
  const foot = <FootRows chain={data.chain} autos={autos} onResults={() => setResults(true)} />;
  const invite = () => setInviting(true);

  const sheets = (
    <>
      <NeedSheet need={open} onClose={() => setOpened(null)} onDone={() => open && finish(open)} onNote={() => open && send(open)} />
      <ResultsSheet chain={data.chain} open={results} onClose={() => setResults(false)} />
      <PeopleSheet now={data.right_now} which={people} onClose={() => setPeople(null)} />
      <NoteSheet
        person={note ? note.person : null}
        about={noteAboutOf(note?.kind === "clicked" ? "nosign" : note?.kind)}
        onClose={() => setNote(null)}
        onSent={() => {
          if (note) void done(note.key, note.at);
        }}
      />
      {inviting && live ? (
        <InviteSheet
          webinar={inviteTargetOf({ id: live.webinar_id, title: live.title, live: { startsAt: live.starts_at, timezone: live.timezone, lengthMin: live.length_min } })}
          onClose={() => setInviting(false)}
          onSent={() => setInviting(false)}
          onToast={(m) => toast(m)}
        />
      ) : null}
    </>
  );

  if (layout === "side") {
    const total = data.chain.customers_connected ? { value: fmt(data.chain.customers), label: "Became customers" } : { value: fmt(data.chain.clicked), label: "Clicked the offer" };
    return (
      <>
        <Panes three className="hs-split">
          <Pane kind="list" keep="home-needs" label="Needs a word">
            {data.faults.length || !data.setup.email ? (
              <Page layout="side" className="hs hs-alerts">
                <Alerts data={data} />
              </Page>
            ) : null}
            <div className="k-sp-lh">
              <div className="k-head hs-lh">
                <h1 className="k-title">
                  Needs a word <small>{needs.length}</small>
                </h1>
              </div>
            </div>
            <Page layout="side" className="hs hs-list">
              <p className="k-sub hs-lsub">
                <WatchingNow count={data.right_now.watching.count} />
              </p>
              {needs.length ? <GroupedList id="hs-needs">{needs.map((n) => row(n, true))}</GroupedList> : null}
            </Page>
          </Pane>
          <Pane kind="main" showing={current?.key ?? null} label="The need that is open">
            <Page layout="side" className="hs">
              {current ? <SideNeed need={current} onNote={() => send(current)} onDone={() => finish(current)} /> : <Empty title="Nobody needs a word">You are all caught up.</Empty>}
            </Page>
          </Pane>
          <Pane kind="detail" keep="home-aside" label="Next live and results">
            <Page layout="side" className="hs">
              <p className="k-eyebrow hs-date">{date}</p>
              {live ? <NextLive live={live} invite="plain" onInvite={invite} /> : null}
              <GroupHead title="Your results" />
              <Metrics
                three
                items={[
                  { value: fmt(data.chain.registered), label: "Registered" },
                  { value: fmt(data.chain.came), label: "Came" },
                  total,
                ]}
              />
              <div className="hs-gap">
                <NowCard now={data.right_now} onPeople={setPeople} />
              </div>
            </Page>
          </Pane>
        </Panes>
        {sheets}
      </>
    );
  }

  const shown = phone && folded ? needs.slice(0, PHONE_NEEDS) : needs;
  const hidden = needs.length - shown.length;
  return (
    <Page layout={layout} narrow className="hs">
      <PageHead eyebrow={date} title={title} right={menu} />
      {sub}
      <Alerts data={data} />
      {layout === "upright" ? (
        <div className="k-two hs-two">
          <div>
            {needsHead}
            {needs.length ? <GroupedList id="hs-needs">{shown.map((n) => row(n, false))}</GroupedList> : <Empty title="Nobody needs a word">You are all caught up.</Empty>}
            <GroupHead title="More" />
            {foot}
          </div>
          <div>
            {live ? (
              <>
                <GroupHead title="Next live" />
                <NextLive live={live} invite="main" onInvite={invite} />
              </>
            ) : null}
            <div className="hs-gap">
              <NowCard now={data.right_now} onPeople={setPeople} />
            </div>
          </div>
        </div>
      ) : (
        <>
          {needsHead}
          {needs.length ? <GroupedList id="hs-needs">{shown.map((n) => row(n, false))}</GroupedList> : <Empty title="Nobody needs a word">You are all caught up.</Empty>}
          {hidden > 0 ? <Fold title={`Show ${hidden} more`} isOpen={false} onClick={() => setFolded(false)} /> : null}
          {live ? (
            <>
              <GroupHead title="Next live" />
              <NextLive live={live} onInvite={invite} />
            </>
          ) : null}
          <div className="hs-gap">
            <NowCard now={data.right_now} onPeople={setPeople} />
          </div>
          <GroupHead title="More" />
          {foot}
          {live ? (
            <DockedBar>
              <Button variant="p" full icon="userplus" onClick={invite}>
                Invite people
              </Button>
            </DockedBar>
          ) : null}
        </>
      )}
      {sheets}
    </Page>
  );
}

/** "6 people are watching right now." */
function WatchingNow({ count }: { count: number }) {
  if (count === 0) return <>No one is watching right now.</>;
  return (
    <>
      <b>{`${fmt(count)} ${count === 1 ? "person" : "people"}`}</b>
      {` ${count === 1 ? "is" : "are"} watching right now.`}
    </>
  );
}

/** The need that is open beside the list (the iPad on its side): who, what they said, and what to do about it. */
function SideNeed({ need, onNote, onDone }: { need: HomeNeed; onNote: () => void; onDone: () => void }) {
  return (
    <div className="hs-d">
      <NeedBody need={need} />
      <div className="hs-btns">
        <AnswerButton need={need} onNote={onNote} />
        <Button icon="check" onClick={onDone}>
          Mark as done
        </Button>
        <Link className="btn q" to={`/people/${need.person.id}`}>
          {`See ${firstName(need.person.name)}`}
        </Link>
      </div>
    </div>
  );
}
